import React from "react";

/**
 * ErrorBoundary — catches runtime JS errors in any child component tree.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 * Props:
 *   - children   — the component tree to protect
 *   - fallback   — optional custom fallback element; renders DefaultFallback if omitted
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error("[ErrorBoundary] Caught render error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? <DefaultFallback error={this.state.error} />;
    }
    return this.props.children;
  }
}

function DefaultFallback({ error }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[200px] px-6 py-10 text-center">
      <div className="rounded-xl border border-red-900/30 bg-[#111] px-8 py-6 max-w-md w-full">
        <div className="mb-3 text-red-400 text-2xl">⚠</div>
        <p className="text-[#fafafa] font-medium mb-1">Something went wrong</p>
        <p className="text-white/40 text-sm mb-5">
          This section failed to load. You can try reloading the page.
        </p>
        {error && (
          <p className="text-white/20 text-xs font-mono mb-4 break-all">
            {error.message}
          </p>
        )}
        <button
          onClick={() => window.location.reload()}
          className="rounded-lg bg-white/10 hover:bg-white/20 transition-colors px-4 py-2 text-sm text-[#fafafa] font-medium"
        >
          Reload page
        </button>
      </div>
    </div>
  );
}
